import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import WorkerLayout from '../../layouts/WorkerLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Input from '../../components/common/Input';
import SecuritySection from '../../components/common/SecuritySection';
import toast from 'react-hot-toast';
import { STATES, getDistricts } from '../../data/indiaLocations';

const SEL = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none';
const INP = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none';

export default function WorkerProfile() {
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({});
  const [selState, setSelState]     = useState('');
  const [selDistrict, setSelDistrict] = useState('');

  useEffect(() => {
    api.get('/worker/profile')
      .then((r) => {
        const p = r.data.data;
        setProfile(p);
        setForm(p);
        setSelState(p.state || '');
        setSelDistrict(p.district || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  function cancelEdit() {
    setEditing(false);
    setForm(profile);
    setSelState(profile.state || '');
    setSelDistrict(profile.district || '');
  }

  async function handleSave() {
    if (!selState || !selDistrict) { toast.error('Please select State and District'); return; }
    setSaving(true);
    try {
      const locRes = await api.post('/lookup/location-resolve', {
        state: selState, district: selDistrict,
        city_village: form.city_village || null,
        pincode: form.pincode || null,
      });
      const location_id = locRes.data.data.id;

      await api.put('/worker/profile', {
        full_name:                form.full_name,
        guardian_name:            form.guardian_name,
        gender:                   form.gender,
        dob:                      form.dob ? form.dob.slice(0, 10) : null,
        address_line:             form.address_line,
        pincode:                  form.pincode,
        location_id,
        experience_years:         form.experience_years,
        daily_wage_min:           form.daily_wage_min,
        daily_wage_max:           form.daily_wage_max,
        travel_radius_km:         form.travel_radius_km,
        availability_status:      form.availability_status,
        upi_id:                   form.upi_id,
        emergency_contact_name:   form.emergency_contact_name,
        emergency_contact_mobile: form.emergency_contact_mobile,
      });
      const updated = { ...profile, ...form, state: selState, district: selDistrict, location_id };
      setProfile(updated);
      setForm(updated);
      setEditing(false);
      toast.success('Profile updated');
    } catch {}
    finally { setSaving(false); }
  }

  if (loading) {
    return (
      <WorkerLayout>
        <div className="flex justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
        </div>
      </WorkerLayout>
    );
  }

  if (!profile) {
    return (
      <WorkerLayout>
        <div className="text-center py-16">
          <span className="text-4xl">👤</span>
          <p className="text-gray-500 mt-3 mb-4">You haven't created a profile yet</p>
          <Link to="/worker/profile/create"><Button>Create Profile</Button></Link>
        </div>
      </WorkerLayout>
    );
  }

  const isExpired = profile.expiry_date && new Date(profile.expiry_date) < new Date();

  return (
    <WorkerLayout>
      <div className="space-y-5 pb-20 sm:pb-6 max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">My Profile</h1>
          {!editing ? (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={cancelEdit}>Cancel</Button>
              <Button size="sm" loading={saving} onClick={handleSave}>Save</Button>
            </div>
          )}
        </div>

        {/* ID card banner */}
        <div className="bg-gradient-to-r from-brand-800 to-brand-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold flex-shrink-0">
              {profile.full_name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="font-bold text-lg leading-tight">{profile.full_name}</h2>
              <p className="text-brand-100 text-xs mt-0.5">{profile.registration_number}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <Badge label={profile.verification_status} variant={profile.verification_status} />
                <Badge label={profile.availability_status} variant={profile.availability_status} />
              </div>
            </div>
          </div>
          {profile.expiry_date && (
            <p className={`text-xs mt-3 ${isExpired ? 'text-red-300' : 'text-brand-200'}`}>
              {isExpired ? 'Expired: ' : 'Valid until: '}
              {new Date(profile.expiry_date).toLocaleDateString('en-IN')}
            </p>
          )}
        </div>

        {/* Alerts */}
        {!profile.expiry_date && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex gap-3">
            <span className="text-xl">💳</span>
            <div>
              <p className="font-medium text-yellow-800 text-sm">Activate your profile</p>
              <p className="text-xs text-yellow-700 mt-0.5">Pay ₹10 registration fee to become visible to employers.</p>
              <Link to="/worker/payment/registration"><Button size="sm" className="mt-2">Pay ₹10 Now</Button></Link>
            </div>
          </div>
        )}
        {isExpired && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
            <span className="text-xl">⛔</span>
            <div>
              <p className="font-medium text-red-800 text-sm">Registration expired</p>
              <Link to="/worker/payment/renewal"><Button size="sm" className="mt-2">Renew for ₹10</Button></Link>
            </div>
          </div>
        )}

        {/* Personal Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h3 className="font-semibold text-gray-900 text-sm">Personal Information</h3>
          {editing ? (
            <div className="space-y-3">
              <Input label="Full Name" required value={form.full_name || ''} onChange={set('full_name')} />
              <Input label="Guardian / Father Name" value={form.guardian_name || ''} onChange={set('guardian_name')} />
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Gender</label>
                <select className={SEL} value={form.gender || ''} onChange={set('gender')}>
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <Input label="Date of Birth" type="date" value={form.dob ? form.dob.slice(0, 10) : ''} onChange={set('dob')} />
              <Input label="Emergency Contact Name" value={form.emergency_contact_name || ''} onChange={set('emergency_contact_name')} />
              <Input label="Emergency Contact Mobile" value={form.emergency_contact_mobile || ''} onChange={set('emergency_contact_mobile')} />
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              {[
                ['Name', profile.full_name],
                ['Guardian', profile.guardian_name],
                ['Gender', profile.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : null],
                ['Date of Birth', profile.dob ? new Date(profile.dob).toLocaleDateString('en-IN') : null],
                ['Emergency Contact', profile.emergency_contact_name],
                ['Emergency Mobile', profile.emergency_contact_mobile],
              ].map(([label, val]) => (
                <div key={label}>
                  <dt className="text-gray-400 text-xs">{label}</dt>
                  <dd className="font-medium text-gray-900 mt-0.5 text-sm">{val || '—'}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Address */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h3 className="font-semibold text-gray-900 text-sm">Address</h3>
          {editing ? (
            <div className="space-y-3">
              {/* State */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">State <span className="text-red-500">*</span></label>
                <select className={SEL} value={selState}
                  onChange={(e) => { setSelState(e.target.value); setSelDistrict(''); }}>
                  <option value="">— Select State / UT —</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {/* District */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">District <span className="text-red-500">*</span></label>
                <select className={SEL} value={selDistrict} disabled={!selState}
                  onChange={(e) => setSelDistrict(e.target.value)}>
                  <option value="">— Select District —</option>
                  {getDistricts(selState).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                {!selState && <p className="text-xs text-gray-400">Select state first</p>}
              </div>
              <Input label="Village / Town / City" value={form.city_village || ''} onChange={set('city_village')} placeholder="Your locality" />
              <Input label="Full Address" value={form.address_line || ''} onChange={set('address_line')} placeholder="House No., Street, Mohalla" />
              <Input label="Pincode" value={form.pincode || ''} onChange={set('pincode')} inputMode="numeric" maxLength={6} />
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              {[
                ['State', profile.state],
                ['District', profile.district],
                ['Village / Town', profile.city_village],
                ['Address', profile.address_line],
                ['Pincode', profile.pincode],
              ].map(([label, val]) => (
                <div key={label}>
                  <dt className="text-gray-400 text-xs">{label}</dt>
                  <dd className="font-medium text-gray-900 mt-0.5 text-sm">{val || '—'}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Work Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h3 className="font-semibold text-gray-900 text-sm">Work Details</h3>
          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="Experience (years)" type="number" min="0" value={form.experience_years ?? ''} onChange={set('experience_years')} />
                <Input label="Travel Radius (km)" type="number" min="0" value={form.travel_radius_km ?? ''} onChange={set('travel_radius_km')} hint="0 = local only" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="Daily Wage Min (₹)" type="number" value={form.daily_wage_min ?? ''} onChange={set('daily_wage_min')} />
                <Input label="Daily Wage Max (₹)" type="number" value={form.daily_wage_max ?? ''} onChange={set('daily_wage_max')} />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Availability</label>
                <select className={SEL} value={form.availability_status || 'available'} onChange={set('availability_status')}>
                  <option value="available">Available</option>
                  <option value="unavailable">Not Available</option>
                  <option value="on_job">Currently on a Job</option>
                </select>
              </div>
              <Input label="UPI ID" value={form.upi_id || ''} placeholder="yourname@upi" onChange={set('upi_id')} />
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              {[
                ['Experience', `${profile.experience_years || 0} yrs`],
                ['Travel Radius', `${profile.travel_radius_km || 0} km`],
                ['Daily Wage', profile.daily_wage_min ? `₹${profile.daily_wage_min}${profile.daily_wage_max ? `–${profile.daily_wage_max}` : ''}` : null],
                ['Availability', profile.availability_status],
                ['UPI ID', profile.upi_id],
              ].map(([label, val]) => (
                <div key={label}>
                  <dt className="text-gray-400 text-xs">{label}</dt>
                  <dd className="font-medium text-gray-900 mt-0.5 text-sm">{val || '—'}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Skills */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm">Skills</h3>
            <Link to="/worker/profile/create" className="text-xs text-brand-600 hover:underline">Update →</Link>
          </div>
          {profile.skills?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {profile.skills.map((s) => (
                <span key={s.skill_id} className="px-2.5 py-1 bg-brand-50 text-brand-700 rounded-full text-xs font-medium">
                  {s.skill_name}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No skills added yet</p>
          )}
        </div>

        {/* Languages */}
        {profile.languages?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">Languages</h3>
            <div className="flex flex-wrap gap-2">
              {profile.languages.map((l) => (
                <span key={l.language_id} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                  {l.language_name}
                </span>
              ))}
            </div>
          </div>
        )}

        <SecuritySection readOnlyEmail />
      </div>
    </WorkerLayout>
  );
}
